import React, { useEffect, useState } from 'react';
import { confirmAction } from '../../utils/confirm';
import {
    createInspectionSchedule, updateInspectionSchedule, deleteInspectionSchedule,
    InspectionTemplate, InspectionSchedule,
} from '../../services/inspectionsApi';
import { fetchZones, fetchAlertContacts } from '../../services/api';
import { fetchContactGroups, ContactGroup } from '../../services/alertsApi';
import ScheduleEditor, { intervalText } from './ScheduleEditor';
import { btn, btnSmall, inGroup } from './common';
import { ui } from '../../styles/shared';
import { fmtDate } from '../../utils/format';

/**
 * Fliken "Scheman": lista över scheman och redigeraren för att skapa/ändra.
 * Mallar och scheman kommer från föräldern eftersom fler flikar använder dem.
 */
interface Props {
    group: string;                 // '' = alla grupper
    templates: InspectionTemplate[];
    schedules: InspectionSchedule[];
    canEdit: boolean;
    /** Laddar om mallar, scheman och räknare i föräldern. */
    reload: () => Promise<void>;
    onError: (msg: string) => void;
}

const SchedulesTab: React.FC<Props> = ({ group, templates, schedules, canEdit, reload, onError }) => {
    const [editSchedule, setEditSchedule] = useState<InspectionSchedule | null | 'new'>(null);
    const [zones, setZones] = useState<{ id: number; name: string; kind?: 'zone' | 'area'; parent_id?: number | null; child_count?: number }[]>([]);
    const [contacts, setContacts] = useState<{ id: number; name: string; email?: string | null }[]>([]);
    const [groups, setGroups] = useState<ContactGroup[]>([]);

    // Platser och kontakter behövs bara i redigeraren.
    useEffect(() => {
        if (!canEdit) return;
        // Typ, område och antal zoner följer med, så platsväljaren kan gå område → zon.
        fetchZones().then(z => setZones(z.map((x: any) => ({ id: x.id, name: x.name, kind: x.kind, parent_id: x.parent_id ?? null, child_count: x.child_count ?? 0 })))).catch(() => {});
        fetchAlertContacts().then(c => setContacts(c)).catch(() => {});
        fetchContactGroups().then(g => setGroups(g)).catch(() => {});
    }, [canEdit]);

    const saveSchedule = async (s: Partial<InspectionSchedule>) => {
        if (editSchedule && editSchedule !== 'new') await updateInspectionSchedule(editSchedule.id, s);
        else await createInspectionSchedule(s);
        setEditSchedule(null);
        await reload();
    };
    const removeSchedule = async (s: InspectionSchedule) => {
        if (!confirmAction(`Ta bort schemat "${s.name}"? Öppna kontroller för schemat stängs.`)) return;
        try { await deleteInspectionSchedule(s.id); await reload(); } catch (e: any) { onError(e.message); }
    };

    const gSchedules = schedules.filter(s => inGroup(group, s.group));
    // Mallvalet följer gruppfiltret ovanför flikarna. Vid ändring av ett schema vars
    // mall ligger utanför vald grupp får mallen ändå vara med, så valet inte ser tomt ut.
    const editTemplateId = editSchedule && editSchedule !== 'new' ? editSchedule.template_id : null;
    const gTemplates = templates.filter(t => t.active && (inGroup(group, t.category) || t.id === editTemplateId));

    return (
        <div>
            {editSchedule && (
                <ScheduleEditor schedule={editSchedule === 'new' ? null : editSchedule} templates={gTemplates}
                    zones={zones} contacts={contacts} groups={groups} onSave={saveSchedule} onCancel={() => setEditSchedule(null)} />
            )}
            {canEdit && !editSchedule && (
                <div style={{ marginBottom: 14 }}>
                    <button onClick={() => setEditSchedule('new')} style={btn('#28a745')} disabled={!gTemplates.length}>+ Nytt schema</button>
                    {!gTemplates.length && <span style={{ fontSize: 13, color: '#888', marginLeft: 10 }}>
                        {templates.length ? 'Ingen mall i den valda gruppen.' : 'Skapa en mall först.'}
                    </span>}
                </div>
            )}
            {gSchedules.length === 0 ? (
                <div style={{ ...ui.card, color: '#666' }}>Inga scheman ännu. Ett schema säger hur ofta en mall ska göras och vem som får påminnelsen.</div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white' }}>
                        <thead><tr><th style={ui.th}>Schema</th><th style={ui.th}>Mall</th><th style={ui.th}>Plats</th><th style={ui.th}>Hur ofta</th><th style={ui.th}>Nästa</th><th style={ui.th}>Ansvarig</th><th style={ui.th}></th></tr></thead>
                        <tbody>
                            {gSchedules.map(s => (
                                <tr key={s.id} style={{ opacity: s.active ? 1 : 0.55 }}>
                                    <td style={ui.td}><b>{s.name}</b>{!s.active && <div style={{ fontSize: 12, color: '#777' }}>pausat</div>}</td>
                                    <td style={ui.td}>{s.template_name}<div style={{ fontSize: 12, color: '#777' }}>{s.group}</div></td>
                                    <td style={ui.td}>
                                        {s.is_round
                                            ? <><b>Rond</b>, {s.place_count} platser<div style={{ fontSize: 12, color: '#777', maxWidth: 260 }}>{s.zone_names.join(', ')}</div></>
                                            : (s.zone_name ?? '–')}
                                    </td>
                                    <td style={ui.td}>{intervalText(s.interval_unit, s.interval_count)}<div style={{ fontSize: 12, color: '#777' }}>påminn {s.remind_days_before} d innan</div></td>
                                    <td style={ui.td}>{fmtDate(s.next_due_date)}{s.open_count > 0 && <div style={{ fontSize: 12, color: '#0d6efd' }}>{s.open_count} öppen</div>}</td>
                                    <td style={ui.td}>
                                        {s.responsible_group_name
                                            ? <>{s.responsible_group_name} <span style={{ fontSize: 12, color: '#777' }}>(grupp)</span></>
                                            : (s.responsible_name ?? '–')}
                                        {(s.escalation_group_name || s.escalation_name) && (
                                            <div style={{ fontSize: 12, color: '#777' }}>
                                                sedan {s.escalation_group_name ? `${s.escalation_group_name} (grupp)` : s.escalation_name}{s.escalation_on_deviation ? ' (även avvikelser)' : ''}
                                            </div>
                                        )}
                                    </td>
                                    <td style={{ ...ui.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                        {canEdit && <button onClick={() => setEditSchedule(s)} style={{ ...btnSmall('#e8f0fe', '#1a4aa6'), marginRight: 6 }}>Ändra</button>}
                                        {canEdit && <button onClick={() => removeSchedule(s)} style={btnSmall('#f8d7da', '#721c24')}>Ta bort</button>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default SchedulesTab;
